import React, { useMemo } from "react";
import { Card, Spin } from "antd";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useGetTopCategoriesQuery } from "@/services/api/dashboard/dashboardApi";

const TopPerformingCategories = () => {
  const {
    data: topCategories,
    isLoading: topCatsLoading,
    isError: topCatsError,
  } = useGetTopCategoriesQuery();

  // Transform API data to radar chart format
  const radarData = useMemo(() => {
    if (!topCategories?.top_categories) return [];

    const categories = topCategories.top_categories;

    // Calculate total orders across all categories
    const totalOrders = categories.reduce(
      (sum, cat) => sum + cat.total_orders,
      0
    );
    // console.log("Total Orders:", totalOrders);
    // Transform to radar chart format with percentages
    return categories.map((cat) => ({
      category: cat.category_name || "Uncategorized",
      performance: ((cat.total_orders / totalOrders) * 100).toFixed(1),
      totalOrders: cat.total_orders,
      totalAmount: cat.total_amount,
      orderType: cat.order_type,
    }));
    // .filter((cat) => cat.category !== "Uncategorized"); // Optional: filter out null categories
  }, [topCategories]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-black p-3 border rounded shadow-lg">
          <p className="font-medium text-white">{label}</p>
          <p className="text-sm text-white">Performance: {payload[0].value}%</p>
          <p className="text-xs text-gray-300">Orders: {data.totalOrders}</p>
          <p className="text-xs text-gray-300">
            Amount: ₦{parseFloat(data.totalAmount).toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  if (topCatsLoading) {
    return (
      <Card
        title="Top performing Orders by Categories"
        className="shadow-sm border border-gray-100"
      >
        <Spin size="large" />
      </Card>
    );
  }

  if (topCatsError) {
    return (
      <Card
        title="Top performing Orders by Categories"
        className="shadow-sm border border-gray-100"
      >
        <div className="h-64 flex items-center justify-center">
          <p className="text-red-500">Error loading data</p>
        </div>
      </Card>
    );
  }

  if (radarData.length === 0) {
    return (
      <Card
        title="Top performing Orders by Categories"
        className="shadow-sm border border-gray-100"
      >
        <div className="h-64 flex items-center justify-center">
          <p className="text-gray-500">No data available</p>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Top performing Orders by Categories"
      className="shadow-sm border border-gray-100"
      styles={{ body: { padding: "24px" } }}
    >
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart
            data={radarData}
            margin={{ top: 20, right: 60, bottom: 20, left: 60 }}
          >
            {/* Grid lines */}
            <PolarGrid stroke="#E5E7EB" strokeWidth={1} gridType="polygon" />

            {/* Category labels around the chart */}
            <PolarAngleAxis
              dataKey="category"
              tick={{
                fontSize: 12,
                fill: "#6B7280",
                fontWeight: 500,
              }}
              className="text-gray-600"
            />

            {/* Radial axis (hidden for cleaner look) */}
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={false}
              axisLine={false}
            />

            {/* Custom tooltip */}
            <Tooltip content={<CustomTooltip />} />

            {/* The radar area */}
            <Radar
              dataKey="performance"
              stroke="#F59E0B"
              fill="#F59E0B"
              fillOpacity={0.6}
              strokeWidth={2}
              dot={{
                fill: "#F59E0B",
                strokeWidth: 2,
                r: 4,
                stroke: "#fff",
              }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Performance legend/summary */}
      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-center justify-center space-x-8 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-[#F59E0B] rounded-full opacity-60"></div>
            <span className="text-gray-600">Performance Score</span>
          </div>
          <div className="text-gray-500 text-xs">
            Based on order volume distribution
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TopPerformingCategories;
